import React, { useState, useEffect, useMemo } from 'react';
import { Clock, Search, Filter, Building2, User, AlertTriangle, CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { Input } from '../components/ui/input';
import { adminApi } from '../services/api';

const STATUS_CONFIG = {
  active: { label: 'Active', bg: 'bg-emerald-500/15', text: 'text-emerald-400', border: 'border-emerald-500/30' },
  due_soon: { label: 'Due Soon', bg: 'bg-amber-500/15', text: 'text-amber-400', border: 'border-amber-500/30' },
  expiring_soon: { label: 'Expiring Soon', bg: 'bg-red-500/15', text: 'text-red-400', border: 'border-red-500/30' },
  expired: { label: 'Expired', bg: 'bg-slate-500/15', text: 'text-slate-400', border: 'border-slate-500/30' },
};

const StatusBadge = ({ status }) => {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.active;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full border ${config.bg} ${config.text} ${config.border}`}
      data-testid={`status-badge-${status}`}>
      {status === 'active' && <CheckCircle2 className="w-3 h-3" />}
      {status === 'due_soon' && <Clock className="w-3 h-3" />}
      {status === 'expiring_soon' && <AlertTriangle className="w-3 h-3" />}
      {status === 'expired' && <XCircle className="w-3 h-3" />}
      {config.label}
    </span>
  );
};

const DaysDisplay = ({ days }) => {
  if (days < 0) {
    return <span className="text-red-400 font-semibold text-sm">{Math.abs(days)}d overdue</span>;
  }
  if (days <= 30) {
    return <span className="text-red-400 font-semibold text-sm">{days}d left</span>;
  }
  if (days <= 60) {
    return <span className="text-amber-400 font-semibold text-sm">{days}d left</span>;
  }
  return <span className="text-emerald-400 font-medium text-sm">{days}d left</span>;
};

const TABS = [
  { id: 'all', label: 'All Services', filter: () => true },
  { id: 'due_soon', label: 'Due Soon (60d)', filter: r => r.status === 'due_soon' || r.status === 'expiring_soon' },
  { id: 'expiring_soon', label: 'Expiring Soon (30d)', filter: r => r.status === 'expiring_soon' },
  { id: 'expired', label: 'Expired', filter: r => r.status === 'expired' },
];

const ExpiryTrackerPage = ({ onNavigateToCompany, onNavigateToDirector }) => {
  const [allRecords, setAllRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [serviceFilter, setServiceFilter] = useState('all');
  const [entityFilter, setEntityFilter] = useState('all');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const data = await adminApi.getExpiryTrackerAll();
      setAllRecords(data || []);
    } catch (error) {
      console.error('Failed to load expiry data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Dynamically build service type options from data
  const serviceTypes = useMemo(() => {
    const types = new Set(allRecords.map(r => r.service_type));
    return Array.from(types).sort();
  }, [allRecords]);

  // Filter records for active tab
  const currentTab = TABS.find(t => t.id === activeTab);
  const filteredRecords = useMemo(() => {
    let records = allRecords.filter(currentTab.filter);
    if (serviceFilter !== 'all') {
      records = records.filter(r => r.service_type === serviceFilter);
    }
    if (entityFilter !== 'all') {
      records = records.filter(r => r.entity_type === entityFilter);
    }
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      records = records.filter(r =>
        r.entity_name?.toLowerCase().includes(q) ||
        r.company_name?.toLowerCase().includes(q) ||
        r.user_name?.toLowerCase().includes(q) ||
        r.service_type?.toLowerCase().includes(q)
      );
    }
    return records;
  }, [allRecords, activeTab, serviceFilter, entityFilter, searchTerm]);

  // Tab counts
  const tabCounts = useMemo(() => ({
    all: allRecords.length,
    due_soon: allRecords.filter(TABS[1].filter).length,
    expiring_soon: allRecords.filter(TABS[2].filter).length,
    expired: allRecords.filter(TABS[3].filter).length,
  }), [allRecords]);

  const handleEntityClick = (record) => {
    if (record.entity_type === 'Company' && record.company_id && onNavigateToCompany) {
      onNavigateToCompany({ id: record.company_id, company_name: record.company_name });
    } else if (record.entity_type === 'Director' && record.director_id && onNavigateToDirector) {
      onNavigateToDirector(record.director_id, record.company_id);
    }
  };

  return (
    <div className="space-y-6" data-testid="expiry-tracker-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Clock className="w-7 h-7 text-amber-400" />
            Expiry Tracker
          </h1>
          <p className="text-slate-400 text-sm mt-1">Monitor service renewals and compliance deadlines</p>
        </div>
        <button
          onClick={loadData}
          disabled={loading}
          className="px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-slate-300 hover:bg-slate-700 text-sm transition-colors"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Refresh'}
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-800/50 rounded-xl p-1 border border-slate-700/50">
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            data-testid={`tab-${tab.id}`}
            className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${
              activeTab === tab.id
                ? tab.id === 'expired'
                  ? 'bg-red-500/20 text-red-300 border border-red-500/30'
                  : tab.id === 'expiring_soon'
                  ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30'
                  : tab.id === 'all'
                  ? 'bg-indigo-500/20 text-indigo-300 border border-indigo-500/30'
                  : 'bg-blue-500/20 text-blue-300 border border-blue-500/30'
                : 'text-slate-400 hover:text-slate-200 border border-transparent'
            }`}
          >
            {tab.label}
            {tabCounts[tab.id] > 0 && (
              <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                activeTab === tab.id ? 'bg-white/10' : 'bg-slate-700'
              }`}>
                {tabCounts[tab.id]}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <Input
            placeholder="Search by name, company, or client..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
            data-testid="expiry-search"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-slate-500" />
          <select
            value={serviceFilter}
            onChange={(e) => setServiceFilter(e.target.value)}
            className="bg-slate-800 border border-slate-700 rounded-lg text-sm text-white px-3 py-2"
            data-testid="filter-service-type"
          >
            <option value="all">All Services</option>
            {serviceTypes.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select
            value={entityFilter}
            onChange={(e) => setEntityFilter(e.target.value)}
            className="bg-slate-800 border border-slate-700 rounded-lg text-sm text-white px-3 py-2"
            data-testid="filter-entity-type"
          >
            <option value="all">All Types</option>
            <option value="Director">Director</option>
            <option value="Company">Company</option>
          </select>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-slate-500" />
          <span className="ml-3 text-slate-400">Loading expiry data...</span>
        </div>
      ) : filteredRecords.length === 0 ? (
        <div className="text-center py-16 bg-slate-800/30 rounded-xl border border-slate-700/50">
          <Clock className="w-12 h-12 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-400 text-lg">
            {activeTab === 'expired' ? 'No expired services' :
             activeTab === 'expiring_soon' ? 'No services expiring within 30 days' :
             activeTab === 'all' ? 'No tracked services found' :
             'No services due within 60 days'}
          </p>
          <p className="text-slate-500 text-sm mt-1">All services are healthy</p>
        </div>
      ) : (
        <div className="bg-slate-800/30 border border-slate-700/50 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="expiry-table">
              <thead>
                <tr className="border-b border-slate-700/50">
                  <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wider px-4 py-3">Type</th>
                  <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wider px-4 py-3">Name</th>
                  <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wider px-4 py-3">Client</th>
                  <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wider px-4 py-3">Service</th>
                  <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wider px-4 py-3">Expiry Date</th>
                  <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wider px-4 py-3">Remaining</th>
                  <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wider px-4 py-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredRecords.map((record, idx) => (
                  <tr key={`${record.source_id}-${record.service_type}-${idx}`}
                    className="border-b border-slate-700/30 hover:bg-slate-800/50 transition-colors"
                    data-testid={`expiry-row-${idx}`}
                  >
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded-md ${
                        record.entity_type === 'Director'
                          ? 'bg-purple-500/15 text-purple-400'
                          : 'bg-blue-500/15 text-blue-400'
                      }`}>
                        {record.entity_type === 'Director'
                          ? <User className="w-3 h-3" />
                          : <Building2 className="w-3 h-3" />}
                        {record.entity_type}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => handleEntityClick(record)}
                        className="text-white hover:text-indigo-400 font-medium text-sm transition-colors text-left"
                        data-testid={`entity-link-${idx}`}
                      >
                        {record.entity_name}
                      </button>
                      {record.entity_type === 'Director' && record.company_name && (
                        <p className="text-xs text-slate-500 mt-0.5">{record.company_name}</p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-300">{record.user_name || '—'}</td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-white font-medium">{record.service_type}</span>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-300">
                      {record.expiry_date
                        ? new Date(record.expiry_date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
                        : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <DaysDisplay days={record.days_remaining} />
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={record.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-3 border-t border-slate-700/50 text-xs text-slate-500">
            Showing {filteredRecords.length} record{filteredRecords.length !== 1 ? 's' : ''}
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpiryTrackerPage;
